// Convert an IContentInfo's content type to a DataQualityDetailProps asset type.
import {
  DataQualityAssetType,
  WarningStatusIcon
} from '@tableau/data-catalog-client-components';
import { SiteUrls } from '@tableau/shared-urls';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';

import { dataQualityIndicatorInfoToDataQualityIndicator } from '~src/ts/models/IDataQualityIndicatorInfo';

import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { CERTIFIED_INDICATOR_TYPE, dataQualityIndicatorInfosToProps } from '../../../quality/DataQualityType';

export function contentTypeToAssetType(info: IContentInfo): DataQualityAssetType | null {
  switch (info.contentType) {
    case 'datasource':
      return 'PublishedDatasource';
    case 'flow':
      return 'Flow';
    case 'dataconnection':
      return 'WebDataConnector';
    case 'database':
    case 'databaseWithCrass':
      return 'DatabaseServer';
    case 'table':
    case 'tableWithCrass':
      return 'DatabaseTable';
    default:
      return null;
  }
}

// Create an appropriate URL for a particular content type.
export function urlForContentType(siteUrls: SiteUrls, info: IContentInfo): string | null {
  switch (info.contentType) {
    case 'datasource':
      return siteUrls.datasourceUrl(info.id);
    case 'flow':
      return siteUrls.flowUrl(info.id);
    case 'database':
    case 'databaseWithCrass':
      return siteUrls.catalogDatabaseUrl(info.id);
    case 'table':
    case 'tableWithCrass':
      return siteUrls.catalogTableUrl(info.id);
    case 'publishedConnection':
      return siteUrls.publishedConnectionUrl(info.id);
    case 'publishedConnectionTable':
      return siteUrls.publishedConnectionTableUrl(info.id);
    default:
      return null;
  }
}

export function DataQualityWarningCell(siteUrls: SiteUrls, hasDataQualityIndicator: (row: DataGrid.Row) => boolean):
  React.Factory<BodyCell.Props> {
  return React.createFactory(class extends React.Component<BodyCell.Props, any> {
    render() {
      if (!hasDataQualityIndicator(this.props.row)) {
        return null;
      }

      const contentTypeWithDQW = this.props.row.data as IContentInfo;
      const assetUrl = urlForContentType(siteUrls, contentTypeWithDQW);
      const assetType = contentTypeToAssetType(contentTypeWithDQW);
      if (!['PublishedDatasource', 'Flow', 'PublishedConnection', 'PublishedConnectionTable'].includes(assetType)) {
        return null;
      }

      const details = dataQualityIndicatorInfosToProps(
        {
          assetUrl: assetUrl,
          assetName: contentTypeWithDQW.name,
          assetType: assetType,
          onContentItem: true
        },
        contentTypeWithDQW.dataQualityIndicators.filter(t => t.type !== CERTIFIED_INDICATOR_TYPE)
      );

      const anchoredWarnings = contentTypeWithDQW.dataQualityIndicators.filter(t => t.type !== CERTIFIED_INDICATOR_TYPE);
      return (
        <span className="tb-quality-indicator-cell" data-tb-test-id='DataQualityIndicatorCell'>
          <WarningStatusIcon
            iconOnly={true}
            urls={siteUrls}
            kind={'vizportal'}
            upstreamWarnings={[]}
            subjectArea={assetType}
            anchoredAsset={{
              assetName: contentTypeWithDQW.name,
              assetType: assetType,
              assetUrl: _ => assetUrl
            }}
            anchoredWarnings={anchoredWarnings.map(i => dataQualityIndicatorInfoToDataQualityIndicator(i))}
          />
        </span>
      );
    }
  });
}
